import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createTextVNode as _createTextVNode, unref as _unref, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "modal-header" }
const _hoisted_2 = { class: "modal-copy" }
const _hoisted_3 = { class: "modal-subtitle" }
const _hoisted_4 = { class: "field-stack" }
const _hoisted_5 = { class: "field-group" }
const _hoisted_6 = {
  key: 0,
  class: "field-error"
}
const _hoisted_7 = { class: "field-group" }
const _hoisted_8 = {
  key: 0,
  class: "field-error"
}
const _hoisted_9 = { class: "modal-actions" }
const _hoisted_10 = { class: "action-button-content" }

import { computed, ref, watch } from "vue";
import { useModalStore } from "@/shared/stores/modal";
import { useSnackbarStore } from "@/shared/stores/snackbar";
import AppButtonFlat from "@/shared/components/ui/AppButtonFlat.vue";
import AppDateField from "@/shared/components/ui/AppDateField.vue";
import AppTimeField from "@/shared/components/ui/AppTimeField.vue";
import { approveAppointmentReschedule } from "@/shared/services/appointments.api";


export default /*@__PURE__*/_defineComponent({
  __name: 'AppointmentRescheduleApproveModal',
  props: {
    appointment: {},
    onSuccess: { type: Function }
  },
  setup(__props: any) {

const props = __props;

const modal = useModalStore();
const snackbar = useSnackbarStore();
const isSubmitting = ref(false);
const preferredDate = ref<Date | string | null>(null);
const preferredTime = ref("");
const dateError = ref("");
const timeError = ref("");

const appointmentNo = computed(() =>
  String(props.appointment?.appointment_no || "this appointment"),
);
const canConfirm = computed(() => Boolean(preferredDate.value) && preferredTime.value.trim().length > 0);

watch(preferredDate, () => {
  if (dateError.value) dateError.value = "";
});

watch(preferredTime, () => {
  if (timeError.value) timeError.value = "";
});

watch(
  () => props.appointment,
  () => {
    preferredDate.value = toDateValue(props.appointment?.reschedule_request?.preferred_date);
    preferredTime.value = normalizeTimeValue(props.appointment?.reschedule_request?.preferred_time);
  },
  { immediate: true },
);

async function handleConfirm() {
  if (isSubmitting.value) return;

  const normalizedDate = toIsoDate(preferredDate.value);
  const normalizedTime = toIsoTime(preferredTime.value);

  if (!normalizedDate) {
    dateError.value = "Please select a date.";
  }
  if (!normalizedTime) {
    timeError.value = "Please select a time.";
  }
  if (!normalizedDate || !normalizedTime) {
    return;
  }

  isSubmitting.value = true;

  try {
    const appointmentId = props.appointment?.id;
    if (!appointmentId) {
      throw new Error("Appointment id is missing.");
    }

    await approveAppointmentReschedule(appointmentId, {
      preferred_date: normalizedDate,
      preferred_time: normalizedTime,
    });

    snackbar.success("Reschedule request approved.");
    if (props.onSuccess) props.onSuccess();
    modal.close();
  } catch (error: any) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      "Unable to approve reschedule request.";
    snackbar.error(message);
  } finally {
    isSubmitting.value = false;
  }
}

function toDateValue(value: string | null | undefined): Date | string | null {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];

  return raw;
}

function normalizeTimeValue(value: string | null | undefined): string {
  if (!value) return "";
  return String(value).trim();
}

function toIsoDate(value: Date | string | null): string {
  if (!value) return "";
  if (typeof value === "string") {
    const raw = value.trim();
    if (!raw) return "";

    const isoDateMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoDateMatch) return isoDateMatch[1];
    return raw;
  }
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function toIsoTime(value: string): string {
  const raw = value.trim();
  if (!raw) return "";

  const twentyFourHourMatch = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (twentyFourHourMatch) {
    const hours = twentyFourHourMatch[1].padStart(2, "0");
    const minutes = twentyFourHourMatch[2];
    const seconds = (twentyFourHourMatch[3] || "00").padStart(2, "0");
    return `${hours}:${minutes}:${seconds}`;
  }

  const twelveHourMatch = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([AaPp][Mm])$/);
  if (twelveHourMatch) {
    let hours = Number(twelveHourMatch[1]);
    const minutes = twelveHourMatch[2];
    const seconds = (twelveHourMatch[3] || "00").padStart(2, "0");
    const period = twelveHourMatch[4].toLowerCase();

    if (period === "pm" && hours < 12) hours += 12;
    if (period === "am" && hours === 12) hours = 0;

    return `${String(hours).padStart(2, "0")}:${minutes}:${seconds}`;
  }

  return raw;
}

return (_ctx: any,_cache: any) => {
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_v_card_text, { class: "modal-body" }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_1, [
          _createElementVNode("div", _hoisted_2, [
            _cache[3] || (_cache[3] = _createElementVNode("p", { class: "modal-title" }, "Approve reschedule request?", -1)),
            _createElementVNode("p", _hoisted_3, " Confirm the new date and time for " + _toDisplayString(appointmentNo.value) + ". ", 1)
          ])
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("div", _hoisted_5, [
            _createVNode(AppDateField, {
              modelValue: preferredDate.value,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((preferredDate).value = $event)),
              label: "Date",
              "label-icon": "mdi-calendar-outline",
              variant: "outlined",
              "hide-details": "auto",
              "prepend-icon": "",
              placeholder: "Select a date"
            }, null, 8, ["modelValue"]),
            (dateError.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_6, _toDisplayString(dateError.value), 1))
              : _createCommentVNode("", true)
          ]),
          _createElementVNode("div", _hoisted_7, [
            _createVNode(AppTimeField, {
              modelValue: preferredTime.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((preferredTime).value = $event)),
              label: "Time",
              "label-icon": "mdi-clock-outline",
              variant: "outlined",
              "hide-details": "auto"
            }, null, 8, ["modelValue"]),
            (timeError.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(timeError.value), 1))
              : _createCommentVNode("", true)
          ])
        ])
      ]),
      _: 1
    }),
    _createVNode(_component_v_card_actions, null, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_9, [
          _createVNode(AppButtonFlat, {
            size: "small",
            color: "secondary",
            variant: "outlined",
            onClick: _cache[2] || (_cache[2] = ($event: any) => (_unref(modal).close())),
            disabled: isSubmitting.value
          }, {
            default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
              _createTextVNode(" Cancel ", -1)
            ]))]),
            _: 1
          }, 8, ["disabled"]),
          _createVNode(AppButtonFlat, {
            size: "small",
            color: "success",
            onClick: handleConfirm,
            disabled: isSubmitting.value || !canConfirm.value
          }, {
            default: _withCtx(() => [
              _createElementVNode("span", _hoisted_10, [
                (isSubmitting.value)
                  ? (_openBlock(), _createBlock(_component_v_progress_circular, {
                      key: 0,
                      indeterminate: "",
                      size: 14,
                      width: 2,
                      class: "action-button-spinner"
                    }))
                  : (_openBlock(), _createBlock(_component_v_icon, {
                      key: 1,
                      size: "16"
                    }, {
                      default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                        _createTextVNode("mdi-check-circle-outline", -1)
                      ]))]),
                      _: 1
                    })),
                _cache[6] || (_cache[6] = _createElementVNode("span", null, "Approve", -1))
              ])
            ]),
            _: 1
          }, 8, ["disabled"])
        ])
      ]),
      _: 1
    })
  ], 64))
}
}

})